import { FC, useEffect, useMemo, useRef } from 'react';
import { Provider, useUnit } from 'effector-react/scope';
import { SelectControl, SelectField } from '@ui/index';
import { PeopleItem, ObjectItem } from '../../interfaces';
import {
  $data,
  $isLoading,
  changePage,
  closeMenu,
  searchChange,
  fxGetList,
  fxGetListWithObject,
  $perPage,
  changeObject,
  createScope,
} from '../../models';

interface SelectFieldProps {
  form: {
    setFieldValue: (name: string, value: object) => void;
  };
  field: {
    name: string;
  };
}

interface Props {
  object?: ObjectItem[];
}

const PeopleSelect: FC<Props> = ({ object, ...props }) => {
  const options = useUnit($data);
  const isLoading = useUnit($isLoading);
  const perPage = useUnit($perPage);
  const getList = useUnit(fxGetList);
  const getListWithObject = useUnit(fxGetListWithObject);
  const changeObjectFn = useUnit(changeObject);
  const searchChangeFn = useUnit(searchChange);
  const changePageFn = useUnit(changePage);
  const closeMenuFn = useUnit(closeMenu);

  const requestKey = useMemo(() => {
    if (!Array.isArray(object)) {
      return `default:${perPage}`;
    }

    const normalized = object
      .map((item) => `${item?.id ?? ''}:${item?.type ?? ''}`)
      .join('|');

    return `object:${perPage}:${normalized}`;
  }, [object, perPage]);

  const lastRequestKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (lastRequestKeyRef.current === requestKey) {
      return;
    }

    lastRequestKeyRef.current = requestKey;

    if (!object) {
      getList({ per_page: perPage });
      return;
    }
    changeObjectFn(object);

    if (
      Array.isArray(object) &&
      object.length > 0 &&
      object.every((item) => Boolean(item?.id))
    ) {
      getListWithObject({
        per_page: perPage,
        objects: object,
      });
    }
  }, [object, perPage, requestKey, getList, getListWithObject, changeObjectFn]);

  const handleInput = (value: string, { action }: { action: string }) => {
    if (action === 'input-change') {
      searchChangeFn(value);
    }
  };

  return (
    <SelectControl
      options={options}
      isLoading={isLoading}
      onMenuScrollToBottom={changePageFn}
      onMenuClose={closeMenuFn}
      onInputChange={handleInput}
      {...props}
    />
  );
};

const PeopleSelectField: FC<SelectFieldProps> = (props) => {
  const onChange = (value: PeopleItem) =>
    props.form.setFieldValue(props.field.name, value);

  const scope = createScope(props.field.name);

  return (
    <Provider value={scope}>
      <SelectField component={<PeopleSelect />} onChange={onChange} {...props} />
    </Provider>
  );
};

export { PeopleSelect, PeopleSelectField };
